import { CircularProgress, Dialog, DialogActions, DialogContent, Stack, Typography } from '@mui/material';

import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import OlDropdown from '@onlive.site/ui/dist/react/dropdown';
import OlIcon from '@onlive.site/ui/dist/react/icon';
import OlMenu from '@onlive.site/ui/dist/react/menu';
import OlMenuItem from '@onlive.site/ui/dist/react/menu-item';
import OlSpinner from '@onlive.site/ui/dist/react/spinner';
import SettingsKey from 'app/profile/settings/utils/settingsTypes';
import { useSettingValue } from 'app/profile/settings/utils/useSettingValue';
import useFlexBoxStyles from 'common/flexBox';
import useOnliveStyles from 'common/onliveStyles';
import { useSnackbar } from 'notistack';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { generateReportAction } from 'redux/slices/reportSlice';
import { makeStyles } from 'tss-react/mui';
import ActionButton from './ActionButton';
import CustomIconButton from './CustomIconButton';

const REPORTS_LINK_ID = 'export-button-reports-link';

const ReportFormat = {
    CSV: 'csv',
    JSON: 'json',
    XLSX: 'xlsx',
};

const ReportFormatIcon = {
    [ReportFormat.JSON]: 'file-earmark-code',
    [ReportFormat.CSV]: 'file-earmark-spreadsheet',
    [ReportFormat.XLSX]: 'file-earmark-excel',
};

const ReportFormatClass = {
    [ReportFormat.JSON]: 'textPurple',
    [ReportFormat.CSV]: 'textBlue',
    [ReportFormat.XLSX]: 'textGreen',
};

const useStyles = makeStyles()((theme) => {
    return {
        root: {
            minWidth: theme.spacing(56),
            minHeight: theme.spacing(20),
        },
        dialogContent: {
            padding: theme.spacing(3, 3, 1),
            display: 'flex',
            flexDirection: 'column',
        },
        dialogActions: {
            padding: theme.spacing(0, 3, 2),
        },
        confirmationMessage: {
            '& a': {
                textDecoration: 'none',
                color: theme.palette.common.link,
                '&:visited': {
                    textDecoration: 'none',
                    color: theme.palette.common.link,
                },
            },
        },
        textPurple: {
            color: '#800080',
        },
        textBlue: {
            color: '#0000FF',
        },
        textGreen: {
            color: '#008000',
        },
    };
});

export default function ReportAction({ filters = {}, type, disabled }) {
    const dispatch = useDispatch();
    const { enqueueSnackbar } = useSnackbar();
    const { t } = useTranslation();
    const timeZone = useSettingValue(SettingsKey.TIMEZONE) || 'Europe/Madrid';
    const [loading, setLoading] = useState(false);
    const [generateReport, setGenerateReport] = useState(false);
    const navigate = useNavigate();
    const { classes: flexBox, cx } = useFlexBoxStyles();
    const { classes } = useStyles();
    const { classes: onlive } = useOnliveStyles();

    const [reportGeneratedAt, setReportGeneratedAt] = useState(0);

    const closeReportDialog = useCallback(() => {
        setReportGeneratedAt(0);
        setGenerateReport(false);
    }, []);

    const onReport = useCallback(
        (format) => {
            setLoading(true);
            setGenerateReport(true);
            const dto = {
                format,
                query: {
                    ...Object.entries(filters).reduce((acc, [key, value]) => {
                        if (value) {
                            acc[key] = value;
                        }
                        return acc;
                    }, {}),
                    startDate: filters?.startDate ? new Date(filters.startDate).toISOString() : null,
                    endDate: filters?.endDate ? new Date(filters.endDate).toISOString() : null,
                },
                timezone: timeZone,
                type,
            };
            dispatch(
                generateReportAction({
                    requestDto: dto,
                    onSuccess: () => {
                        setLoading(false);
                        setReportGeneratedAt(Date.now());
                    },
                    onFailure: (e) => {
                        setLoading(false);
                        closeReportDialog();
                        console.error(e);
                        enqueueSnackbar(t('app.common.error'), { variant: 'error' });
                    },
                }),
            );
        },
        [filters, timeZone, type, dispatch, enqueueSnackbar, t, closeReportDialog],
    );

    return (
        <>
            {loading ? (
                <div className="pt-2 px-2">
                    <OlSpinner />
                </div>
            ) : (
                <OlDropdown placement="bottom">
                    <CustomIconButton id="onlive-button-download" size="small" name="download" slot="trigger">
                        <FileDownloadOutlinedIcon component="svg" />
                    </CustomIconButton>

                    <OlMenu>
                        <OlMenuItem value={ReportFormat.CSV} onClick={() => onReport(ReportFormat.CSV)}>
                            <OlIcon
                                slot="prefix"
                                name={ReportFormatIcon[ReportFormat.CSV]}
                                className={classes[ReportFormatClass[ReportFormat.CSV]]}
                            />
                            CSV
                        </OlMenuItem>
                        <OlMenuItem value={ReportFormat.JSON} onClick={() => onReport(ReportFormat.JSON)}>
                            <OlIcon
                                slot="prefix"
                                name={ReportFormatIcon[ReportFormat.JSON]}
                                className={classes[ReportFormatClass[ReportFormat.JSON]]}
                            />
                            JSON
                        </OlMenuItem>
                        <OlMenuItem value={ReportFormat.XLSX} onClick={() => onReport(ReportFormat.XLSX)}>
                            <OlIcon
                                slot="prefix"
                                name={ReportFormatIcon[ReportFormat.XLSX]}
                                className={classes[ReportFormatClass[ReportFormat.XLSX]]}
                            />
                            XLSX
                        </OlMenuItem>
                    </OlMenu>
                </OlDropdown>
            )}

            {generateReport ? (
                <Dialog
                    open={true}
                    aria-labelledby="alert-dialog-title"
                    aria-describedby="alert-dialog-description"
                    classes={{
                        paper: cx(flexBox.borderRadius1, onlive.boxShadow, classes.root),
                    }}
                >
                    <DialogContent className={classes.dialogContent}>
                        <Typography variant="onliveH5" fontWeight="500" className={flexBox.paddingBottom1}>
                            {t('common.export.title')}
                        </Typography>
                        <Typography variant="subtitle2" fontWeight={400} className={flexBox.marginTop1}>
                            {reportGeneratedAt ? (
                                <div
                                    onClick={(e) => {
                                        const target = e.target;
                                        if (target.id === REPORTS_LINK_ID || target.closest(`#${REPORTS_LINK_ID}`)) {
                                            closeReportDialog();
                                            //dispatch(closeReportDialog());
                                            navigate('/reports');
                                        }
                                    }}
                                    className={classes.confirmationMessage}
                                    dangerouslySetInnerHTML={{
                                        __html: t('common.export.message', {
                                            reportMenu: `<a id="${REPORTS_LINK_ID}" href="#"> ${t(
                                                'menu.configuration',
                                            )} / ${t('menu.reports')} </a> `,
                                        }),
                                    }}
                                />
                            ) : (
                                <Stack
                                    direction="row"
                                    justifyContent="center"
                                    alignItems="center"
                                    className={cx(flexBox.w100, flexBox.h100)}
                                >
                                    <CircularProgress />
                                </Stack>
                            )}
                        </Typography>
                    </DialogContent>
                    {reportGeneratedAt ? (
                        <DialogActions className={classes.dialogActions}>
                            <div
                                className={cx(
                                    flexBox.flex,
                                    flexBox.gap1,
                                    flexBox.justifyContentEnd,
                                    flexBox.paddingTop2,
                                )}
                            >
                                <ActionButton
                                    color="primary"
                                    type="submit"
                                    autoFocus
                                    fitWidth
                                    onClick={() => {
                                        closeReportDialog();
                                    }}
                                >
                                    {t('common.term.OK')}
                                </ActionButton>
                            </div>
                        </DialogActions>
                    ) : null}
                </Dialog>
            ) : null}
        </>
    );
}
